import React, { useState } from "react";
import { MdFilterList, MdOutlineClear, MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from "react-icons/md";
import { AiOutlineCalendar } from "react-icons/ai";
import { addYears } from "date-fns";
import EAButton from "../../shared/Buttons/EAButton";
import EABadge from "../../shared/Badge/EABadge";
import EAScheduleDatePicker from "../../shared/EAScheduleDatePicker/EAScheduleDatePicker";
import EACalendarModal from "../../shared/CalendarModal/EACalendarModal";
import SwimmingFiltersModal from "./SwimmingFiltersModal";
import {
  SwimmingExerciseGroup,
  SwimmingFilters,
  SwimmingInstructor,
} from "../../../models/SwimmingScheduleData";
import useMediaQuery from "@mui/material/useMediaQuery";
import EAIconButton from "../../shared/Buttons/EAIconButton";
import "./style.scss";

interface IProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  filters: SwimmingFilters;
  onFiltersChange: (filters: SwimmingFilters) => void;
  onClearFilters: () => void;
  availableLocations: string[];
  availableInstructors: SwimmingInstructor[];
  availableExerciseGroups: SwimmingExerciseGroup[];
  hideFilters?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isWeekly?: boolean;
  onPreviousPeriod?: () => void;
  onNextPeriod?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  dateRangeLabel?: string;
}

const countActiveFilters = (filters: SwimmingFilters): number => {
  let count = 0;
  if (filters.onlyAvailable) count++;
  if (filters.instructor) count++;
  if (filters.exerciseGroups.length > 0) count++;
  if (filters.courseType !== "ALL") count++;
  if (filters.locations.length > 0) count++;
  return count;
};

const SwimmingBar: React.FC<IProps> = ({
  selectedDate,
  onDateChange,
  filters,
  onFiltersChange,
  onClearFilters,
  availableLocations,
  availableInstructors,
  availableExerciseGroups,
  hideFilters = false,
  minDate,
  maxDate,
  isWeekly = false,
  onPreviousPeriod,
  onNextPeriod,
  canGoPrevious = true,
  canGoNext = true,
  dateRangeLabel,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 980px)");
  const activeCount = countActiveFilters(filters);
  const hasFilters = activeCount > 0;

  const getPreviousTooltip = () => (isMobile ? "Poprzedni dzień" : "Poprzedni tydzień");
  const getNextTooltip = () => (isMobile ? "Następny dzień" : "Następny tydzień");

  const renderDateNavigation = () => {
    if (isWeekly) {
      return (
        <div className="swimming-bar__weekly-navigation">
          <EAIconButton
            title={getPreviousTooltip()}
            icon={<MdOutlineKeyboardArrowLeft />}
            variant="outlined"
            onClick={onPreviousPeriod ?? (() => {})}
            disabled={!canGoPrevious}
          />
          <EAButton
            title={dateRangeLabel ?? ""}
            endIcon={<AiOutlineCalendar />}
            variant="outlined"
            onClick={() => setCalendarOpen(true)}
          />
          <EAIconButton
            title={getNextTooltip()}
            icon={<MdOutlineKeyboardArrowRight />}
            variant="outlined"
            onClick={onNextPeriod ?? (() => {})}
            disabled={!canGoNext}
          />
        </div>
      );
    }

    return (
      <EAScheduleDatePicker
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        requireConfirmation={false}
        minDate={minDate}
        maxDate={maxDate}
      />
    );
  };

  return (
    <>
      <div className={`swimming-bar${hideFilters ? " swimming-bar--no-filters" : ""}`}>
        {!hideFilters && (
        <div className="swimming-bar__section">
          {isMobile ? (
            <>
              <EABadge value={hasFilters ? activeCount : ""}>
                <EAIconButton
                  title="Filtry"
                  icon={<MdFilterList />}
                  variant="icon"
                  onClick={() => setFiltersOpen(true)}
                />
              </EABadge>
              {hasFilters && (
                <EAIconButton
                  title="Wyczyść filtry"
                  icon={<MdOutlineClear />}
                  variant="icon"
                  onClick={onClearFilters}
                />
              )}
            </>
          ) : (
            <>
              <EABadge value={hasFilters ? activeCount : ""}>
                <EAButton
                  title="Filtry"
                  onClick={() => setFiltersOpen(true)}
                  variant="outlined"
                />
              </EABadge>
              {hasFilters && (
                <EAButton
                  title="Wyczyść filtry"
                  onClick={onClearFilters}
                  startIcon={<MdOutlineClear />}
                  variant="text"
                />
              )}
            </>
          )}
        </div>
        )}

        <div className="swimming-bar__section">
          {renderDateNavigation()}
        </div>
      </div>

      {filtersOpen && (
        <SwimmingFiltersModal
          isOpen={true}
          onClose={() => setFiltersOpen(false)}
          filters={filters}
          onApply={onFiltersChange}
          availableLocations={availableLocations}
          availableInstructors={availableInstructors}
          availableExerciseGroups={availableExerciseGroups}
          isWeekly={isWeekly}
        />
      )}

      {calendarOpen && (
        <EACalendarModal
          onClose={() => setCalendarOpen(false)}
          onDatePick={(date) => {
            onDateChange(date);
            setCalendarOpen(false);
          }}
          date={selectedDate}
          title="Wybór tygodnia"
          fromDate={minDate ?? new Date()}
          toDate={maxDate ?? addYears(new Date(), 1)}
        />
      )}
    </>
  );
};

export default SwimmingBar;
